// @flow
/* eslint sort-keys: "error" */

// Redux actions
export const IS_FETCHING = '@common/IS_FETCHING';
export const SET_SETTINGS = '@common/SET_SETTINGS';
export const UPDATE_FILTER = '@common/UPDATE_FILTER';
export const RESET_FILTERS = '@common/RESET_FILTERS';
export const SET_FILTERS = '@common/SET_FILTERS';
export const CONFIRM_DIALOG = '@common/CONFIRM_DIALOG';
export const SET_LAYOUT = '@common/SET_LAYOUT';

// Request IDs
export const REFRESH_TOKEN_ID = 'refresh-token';
export const GET_TOKENS_ID = 'get-tokens';

export const APP = Object.freeze({
  AUTH: 'auth',
  BILLING: 'billing',
  SFPAGES: 'sfpages',
  SOFARM: 'sofarm',
});

export type AppName = $Values<typeof APP>;

export const LAYOUTS = {
  BASE: 'base',
  EMPTY: 'empty',
  JUMBOTRON: 'jumbotron',
};

export type FetchMethod = 'DELETE' | 'GET' | 'HEAD' | 'PATCH' | 'POST' | 'PUT';

export const KNOWN_HTTP_STATUSES = {
  BUSINESS_LOGIC_ERROR: 409,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNAUTHORIZED: 401,
};

export const FETCH_STATUS = Object.freeze({
  ERROR: 'error',
  LOADED: 'loaded',
  LOADING: 'loading',
  PENDING: 'pending',
});

export type FetchStatus = $Values<typeof FETCH_STATUS>;

export const FETCH_AUTH_SETTINGS_ID = 'auth-settings';
export const FETCH_SOFARM_SETTINGS_ID = 'sofarm-settings';
export const FETCH_SFPAGES_SETTINGS_ID = 'sfpages-settings';
export const FETCH_BILLING_SETTINGS_ID = 'billing-settings';

export const TEXT_SERVER_ERROR = 'Internal Server Error';

export const SCOPE = Object.freeze({
  ACCOUNTANT: 'accountant',
  ADMIN: 'admin',
  ADVERTISER: 'advertiser',
  AGENT: 'agent',
  BILLING: 'billing',
  BILLING_OPERATIONS: 'billingOperations',
  BLACK_HAT: 'blackHat',
  CVV_VIEWER: 'cvvViewer',
  KAM: 'kam',
  SALES_MANAGER: 'salesManager',
  SFPAGES: 'sfpages',
  SOFARM: 'sofarm',
  SUPER_ADMIN: 'superadmin',
  TEAMLEAD: 'teamlead',
  USER_MANAGEMENT: 'userManagement',
  VIDEO_VIEWER: 'videoviewer',
});

export type Scope = $Values<typeof SCOPE>;

export const ACCOUNT_TYPES = Object.freeze({
  FACEBOOK: 1,
  GOOGLE: 5,
  PAYPAL: 6,
});

export type AccountType = $Values<typeof ACCOUNT_TYPES>;

export const ACCOUNT_TYPES_ARRAY = [
  { iconClass: 'facebook', id: ACCOUNT_TYPES.FACEBOOK, path: 'facebook', type: 'Facebook' },
  { iconClass: 'google', id: ACCOUNT_TYPES.GOOGLE, path: 'google', type: 'Google' },
  { iconClass: 'paypal', id: ACCOUNT_TYPES.PAYPAL, path: 'paypal', type: 'Paypal' },
];

export const ACCOUNT_STATUSES = {
  ACTIVE: 1,
  BANNED: 3,
  DRAFT: 4,
  SUSPENDED: 2,
};

export const PRODUCT_STATUSES = {
  BP_CREATED: 2,
  CREATED: 1,
  HALTED: 4,
  NEW: 6,
  READY: 3,
  SPICES: 7,
  SUPPORT: 5,
  WARM_UP: 8,
};

export const AD_STATUSES = Object.freeze({
  BANNED: 4,
  BLACK_HAT: 5,
  NOT_STARTED: 1,
  NO_SPEND: 6,
  PAUSED: 7,
  PREPARED: 3,
  VERIFICATION: 8,
  WARM_UP: 2,
  WHITE_HAT: 9,
});

export type AdStatus = $Values<typeof AD_STATUSES>;

export const SALE_STATUSES = {
  BOOKED: 2,
  FREE: 1,
  NOT_FOR_SALE: 4,
  SOLD: 3,
  USED: 5,
};

export const SALE_REASONS = {
  JV_DEAL: 4,
  PROMO: 5,
  REPLACEMENT: 3,
  SALE: 6,
  SELF_SERVICE: 8,
};

export const NOT_FOR_SALE_REASONS = {
  BAD_QUALITY: 1,
  TEST: 2,
};

export const DATE_FORMATS = {
  API: 'YYYY-MM-DD',
  BASE: 'DD-MM-YYYY',
  X: 'x',
};

export const DATE_TIME_FORMATS = {
  API: 'YYYY-MM-DD HH:mm:ss',
  BASE: 'DD-MM-YYYY HH:mm:ss',
  X: 'x',
};

export const SHORT_TIME_DATE_FORMATS = {
  API: 'YYYY-MM-DD HH:mm:ss',
  BASE: 'DD-MM-YYYY HH:mm',
  X: 'x',
};

export const EXP_DATE_FORMAT = 'MM/YY';

// @deprecated use scopes
export const USER_ROLES = {
  ADMIN: 2,
  ADVERTISER: 7,
  AGENT: 5,
  CVV_VIEWVER: 12,
  KAM: 13,
  MANAGER: 4,
  SALES: 11,
  SERVICE: 6,
  SUPER_ADMIN: 10,
  SYSADMIN: 3,
  SYSTEM: 1,
  TEAMLEAD: 8,
  VIDEOVIEWER: 9,
};

export const GENDER = {
  FEMALE: 2,
  MALE: 1,
};

export const GENDER_MAP_ARRAY = [
  { id: GENDER.MALE, name: 'male' },
  { id: GENDER.FEMALE, name: 'female' },
];

export const TAG_KEYS = Object.freeze({
  ACC: 'ACC',
  ADV_GENERAL: 'ADV_GENERAL',
  BPS: 'BPS',
  BR: 'BR',
  CC: 'CC',
  ENV: 'ENVIRONMENT',
  FP: 'FP',
  FRD: 'FRD',
  GENERAL: 'GENERAL',
  NET: 'NET',
});

export type TagKey = $Values<typeof TAG_KEYS>;

export const PAYMENT_SOURCE_TYPES = Object.freeze({
  AD_CARDS_ONE: 21,
  AD_CARDS_POOL: 22,
  AMEX_CB_PLATINUM: 24,
  AMEX_GOLD: 4,
  AMEX_PLATINUM: 5,
  AMEX_STARWOOD: 6,
  AMEX_STC_PLATINUM: 16,
  AMEX_STC_STARWOOD: 15,
  CAPITALONE: 10,
  CAPITALONECB: 23,
  CAPITALONEFG: 17,
  CAPITALONESTC: 18,
  CHASE: 7,
  CHASESTC: 19,
  CITIBANK: 8,
  COINFIDE: 1,
  CROSSCARD: 20,
  NOCTISONE: 9,
  REVOLUT: 25,
  SIMPLED: 2,
  THIRD_PARTY: 3,
  VPAY: 13,
  WELLS_FARGO: 11,
  WIRECARD: 14,
});

export type PaymentSourceType = $Values<typeof PAYMENT_SOURCE_TYPES>;

export const THIRD_PARTY_PAYMENT_SOURCE_TYPES = Object.freeze([
  PAYMENT_SOURCE_TYPES.THIRD_PARTY,
  PAYMENT_SOURCE_TYPES.CHASE,
  PAYMENT_SOURCE_TYPES.CITIBANK,
  PAYMENT_SOURCE_TYPES.CAPITALONE,
  PAYMENT_SOURCE_TYPES.SIMPLED,
  PAYMENT_SOURCE_TYPES.COINFIDE,
  PAYMENT_SOURCE_TYPES.WELLS_FARGO,
  PAYMENT_SOURCE_TYPES.VPAY,
  PAYMENT_SOURCE_TYPES.WIRECARD,
  PAYMENT_SOURCE_TYPES.CHASESTC,
  PAYMENT_SOURCE_TYPES.CAPITALONECB,
  PAYMENT_SOURCE_TYPES.CAPITALONESTC,
  PAYMENT_SOURCE_TYPES.CAPITALONEFG,
]);

export const YES_NO_OPTIONS = [{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }];

export const RENEWAL_PERIOD_OPTIONS = [{ name: 'Auto', value: 1 }, { name: 'No', value: 0 }];

export const PAYMENT_SOURCE_STATUSES = Object.freeze({
  BLOCKED: 3,
  ISSUED: 2,
  ORDERED: 6,
  REQUESTED: 1,
});

export type PaymentSourceStatus = $Values<typeof PAYMENT_SOURCE_STATUSES>;

export const PAYMENT_SOURCE_LINK_STATUSES = Object.freeze({
  BANNED: 3,
  FREE: 1,
  IN_USE: 2,
  USED: 4,
});

export type PaymentSourceLinkStatus = $Values<typeof PAYMENT_SOURCE_LINK_STATUSES>;

export const TRANSACTION_CLASSIFICATION = Object.freeze({
  CARD: 6,
  CARD_LOAD: 7,
  CARD_LOAD_FEE: 8,
  CARD_UNLOAD: 9,
  CARD_UNLOAD_FEE: 10,
  CLOAKER: 13,
  DOMAIN: 11,
  DOMAIN_RENEWAL: 12,
  FACEBOOK: 3,
  GOOGLE: 4,
  MANUAL_TOPUP: 15,
  MANUAL_WITHDRAWAL: 16,
  PAYMENT: 1,
  PREMIUM: 14,
  PROFILE: 2,
  PROFILE_RENEWAL: 5,
});

export const PERMISSIONS = Object.freeze({
  ACCESS_ADVERTISERS: 'access-advertisers-only',

  BILLING_CREATE_NEGATIVE_TRANSACTIONS: 'createNegativeTransactions_Transaction',
  BILLING_CREATE_POSITIVE_TRANSACTIONS: 'createPositiveTransactions_Transaction',
  BILLING_CREATE_TOPUP_INVOICE: 'createTopupInvoice_Advertiser',
  BILLING_CREATE_TOPUP_INVOICE_OWN: 'createTopupInvoice_own_Advertiser',
  BILLING_EDIT_BILLING_INFO_ADVERTISER: 'editBillingInfo_Advertiser',
  BILLING_EDIT_BILLING_INFO_ADVERTISER_OWN: 'editBillingInfo_own_Advertiser',
  BILLING_INDEX_ALL_VOUCHERS: 'indexAllVouchers_Voucher',
  BILLING_INDEX_OWN_VOUCHERS: 'indexOwnVouchers_Voucher',
  BILLING_INDEX_PRODUCT_STATS: 'indexProductStats',
  BILLING_INDEX_PRODUCT_STATS_WITH_OWN_VOUCHERS: 'indexProductWithOwnVouchersStats',

  BLACKHAT_INDEX_ADDITIONAL_FIELDS: 'blackhatIndexAdditionalFields_Profile',
  BLACKHAT_INDEX_ANY_COMMENTS: 'blackhatIndexAnyComments_Profile',
  BLACKHAT_INDEX_COMMON_FIELDS: 'blackhatIndexCommonFields_Profile',
  BLACKHAT_INDEX_SALE_FIELDS: 'blackhatIndexSaleFields_Profile',
  BLACKHAT_INTERFACE: 'blackhat-access',
  BLACKHAT_SHOW_MAIN_PAYMENT_SOURCE_FIELDS: 'blackhatShowMainPaymentSourceFields_Profile',

  BUY_PROFILE: 'buyProfile',
  BUY_PROFILE_WITH_ACCOUNTS: 'buyProfileWithAccounts',

  CAN_EDIT_ADVERTISER_GENERAL_TAGS: 'editAdvertiserGeneralTags_Tag',
  CAN_SET_NOT_FOR_SALE_SALE_STATUS: 'setSaleStatusNotForSale_Profile',
  CHOOSE_NET_SOLUTION_ON_START: 'chooseNetworkSolutionOnStart_Profile',
  CREATE_SHARED_COMMENT: 'create-shared-notes_Comment',
  DEFAULT_FILTER_SALE_STATUS_SOLD: 'defaultFilterSaleStatusSold',
  EDIT_AMI: 'edit_Ami',
  EDIT_PAYMENTS_INTERNAL_FIELD: 'editInternalFields_PaymentSource',
  EDIT_PAYMENTS_INTERNAL_FIELD_OWN: 'editInternalFields_own_PaymentSource',
  EDIT_PAYMENT_SOURCE: 'edit_PaymentSource',
  EDIT_PAYMENT_SOURCE_OWN: 'edit_own_PaymentSource',
  EDIT_USER: 'edit_User',
  EXCEED_PAYMENT_SOURCE_TOP_UP_LIMIT: 'exceedTopUpLimit_PaymentSource',
  GUEST_ACCESS: 'guest-access',
  INDEX_ADVERTISER: 'index_Advertiser',
  INDEX_AMI: 'index_Ami',
  INDEX_AUTOSTART_SESSIONS: 'index_AutostartSession',
  INDEX_CONNECTOR_BOX: 'index_ConnectorBox',
  INDEX_MANAGED_PROFILE_SESSION: 'indexManaged_ProfileSession',
  INDEX_PAYMENTS: 'index_PaymentSource',
  INDEX_PAYMENTS_ALL: 'indexAll_PaymentSource',
  INDEX_PAYMENTS_OWN: 'indexOwn_PaymentSource',
  INDEX_PROFILE: 'index_Profile',
  INDEX_PROFILE_SESSION: 'index_ProfileSession',
  INDEX_SIM: 'index_Sim',
  INDEX_USER: 'index_User',
  INTERACT_PROFILE: 'interact_Profile',
  LOGIN_PROFILE: 'login_Profile',
  MANAGE_PAYMENTS: 'manage_PaymentSource',
  NOCTIS_ONE_OPERATIONS: 'noctisOneOperations_PaymentSource',
  REGENERATE_SCHEDULE: 'regenerateSchedule_Profile',
  RENEW_MANAGED_PROFILE: 'renewalManaged_OrderItem',
  RENEW_OWN_PROFILE: 'renewal_own_OrderItem',
  RENEW_PROFILE: 'renewal_OrderItem',
  REPLACE_PROFILE_WITH_ACCOUNT: 'replaceWithAccounts_Profile',

  SAFEPAGES_ACCESS: 'sp-access',
  SAFEPAGES_BLACKHAT_DOMAIN: 'blackhat_Zone',
  SAFEPAGES_COMBINED_CLOAKER_USE: 'combinedCloakerUse_Zone',
  SAFEPAGES_CREATE_PRELANDING: 'create_PrelandingTemplate',
  SAFEPAGES_LIST_DOMAIN: 'list_Zone',
  SAFEPAGES_LIST_PRELANDING: 'list_PrelandingTemplate',
  SAFEPAGES_RENEW_DOMAIN: 'uiRenew_OrderItem',
  SAFEPAGES_SELL_CLOAKER: 'uiCloakerSell_Zone',
  SAFEPAGES_SELL_DOMAIN: 'uiSell_Zone',
  SAFEPAGES_SET_ADVERTISER_ON_CREATE_PRELANDING: 'setAdvertiserOnCreate_Zone',
  SAFEPAGES_SET_EXPIRES_AT_ORDER_ITEM: 'setExpiresAt_OrderItem',
  SAFEPAGES_SHOW_DOMAIN: 'show_Zone',
  SAFEPAGES_SHOW_DOMAIN_IN_PROFILE: 'uiDomainTab_Zone',
  SAFEPAGES_SHOW_MANAGER_DOMAIN: 'showManager_Zone',
  SAFEPAGES_SHOW_MANAGER_PRELANDING: 'showManager_PrelandingTemplate',
  SAFEPAGES_SHOW_OWNER_DOMAIN: 'showOwner_Zone',
  SAFEPAGES_SHOW_OWNER_PRELANDING: 'showOwner_PrelandingTemplate',
  SAFEPAGES_SHOW_PRELANDING: 'show_PrelandingTemplate',

  SALE_PROFILE: 'sale_Profile',
  SEE_ADD_PROFILE_FIELDS: 'index-additional-fields_Profile',
  SEE_ADV_HELP_LINKS: 'see-advertisers-help-links',
  SEE_COMMENT_PROFILE_FIELD: 'index-related-comments_Profile',
  SEE_COMMON_PROFILE_FIELDS: 'index-common-fields_Profile',
  SEE_DEVICE_INFO: 'can-see-device-info_Device',
  SEE_EMAIL_PROFILE_FIELD: 'index-email-address-field_Profile',
  SEE_HELP_MENU: 'see-help-menu',
  SEE_LAST_ACTIVITY_FIELD: 'index-last-activity-field_Profile',
  SEE_SALE_PROFILE_FIELDS: 'index-sale-fields_Profile',
  SELL_CARD_TO_ADVERTISER: 'sellCardTo_Advertiser',
  SELL_CARD_TO_OWN_ADVERTISER: 'sellCardTo_own_Advertiser',
  SELL_CLOAKER: 'sell_Cloaker',
  SHOW_ADVERTISER: 'show_Advertiser',
  SHOW_ADVERTISER_VOUCHER_LIST: 'showAdvertiserVouchers_Advertiser',
  SHOW_CVV: 'showCvvCode_PaymentSource',
  SHOW_MANAGED_PROFILE_SESSION: 'showManaged_ProfileSession',
  SHOW_OWN_CVV: 'showCvvCode_own_PaymentSource',
  SHOW_PAYMENTS_INTERNAL_FIELD: 'showInternalFields_PaymentSource',
  SHOW_PAYMENTS_INTERNAL_FIELD_OWN: 'showInternalFields_own_PaymentSource',
  SHOW_PROFILE_ANY: 'showAny_Profile',
  SHOW_PROFILE_FREE: 'showFree_Profile',
  SHOW_PROFILE_MANAGED: 'showManaged_Profile',
  SHOW_PROFILE_OWN: 'showAny_own_Profile',
  SHOW_PROFILE_SESSION: 'show_ProfileSession',
  SHOW_SALE_HISTORY_LINK: 'showSaleStatusHistoryLink_Profile',
  SHOW_SIM: 'show_Sim',
  SHOW_WAS_USED_FIELD: 'showWasUsedField_Profile',
  START_EXPIRED_PROFILE: 'startExpired_Profile',
  START_OWN_PROFILE: 'start_own_Profile',
  START_PROFILE: 'start_Profile',
  STOP_OWN_PROFILE: 'stopOccupiedByMe_Profile',
  STOP_PROFILE: 'stop_Profile',
  STORE_SIM: 'store_Sim',
  TOP_UP_OWN_PAYMENTS: 'topUpOwnPaymentSource_own_PaymentSource',
  UPDATE_EXPIRES_AT_ORDER_ITEM: 'updateExpiresAtDate_OrderItem',
  UPDATE_SIM: 'update_Sim',
  UPLOAD_ALL_MEDIA_ON_DEVICE: 'uploadAllMediaOnDevice_Profile',
  UPLOAD_ALL_MEDIA_ON_OWN_DEVICE: 'uploadAllMediaOnDevice_own_Profile',
  UPLOAD_TAGS_MEDIA_TO_DEVICE: 'uploadTagsMediaToDevice_Profile',
  WITHDRAW_OWN_PAYMENTS: 'withdrawOwnPaymentSource_own_PaymentSource',
});

export const FACEBOOK_URL = 'https://facebook.com';

export const COUNTRIES_WITH_STATES = [1];

export const NOTIFY_DELAY = 10;

export const MAX_INT32 = 2147483647;
export const MIN_INT32 = -2147483648;



// WEBPACK FOOTER //
// ./src/common/constants/index.js